import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

interface MobileErrorBoundaryProps {
  children: React.ReactNode;
  onError?: (error: Error) => void;
}

interface MobileErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Memory-aware error boundary for mobile
export class MobileErrorBoundary extends React.Component<
  MobileErrorBoundaryProps,
  MobileErrorBoundaryState
> {
  constructor(props: MobileErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[MobileErrorBoundary] Caught error:', error, errorInfo);
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card className="border-2 border-brand-error/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-brand-error mb-3">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Component Error</span>
            </div>
            <p className="text-xs text-brand-dark-blue/70 mb-3">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <Button
              onClick={() => this.setState({ hasError: false, error: null })}
              size="sm"
              variant="outline"
              className="text-brand-error border-brand-error hover:bg-brand-error/5"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}
